/**
 * Pure tree math for hierarchical team allocation (AIR-572).
 *
 * Semantics (conservation model): a team's `litellm_quota.budgetLimit` is its
 * OWN direct-spend headroom, enforced leaf-only by the proxy. Allocating to a
 * child team moves budget from the parent's row to the child's row inside one
 * transaction, so a subtree's total allocation (Σ of its rows) only changes
 * when the root grant changes. Consequences:
 *   - request-time enforcement needs no tree walk (zero hot-path cost)
 *   - Σ(all team rows + Unassigned pool) ≤ client cap stays the correct
 *     client-level invariant with no query changes
 *   - a node's user-facing "allocation" is its SUBTREE sum (own + descendants)
 */

export interface TeamNode {
  id: string;
  parentTeamId: string | null;
}

export interface TeamTree {
  /** parentId -> ordered child ids. Roots live under the ROOT_KEY. */
  childrenOf: Map<string | null, string[]>;
  /** Depth-first pre-order over all nodes (parents before children). */
  dfsOrder: string[];
  depthOf: Map<string, number>;
  parentOf: Map<string, string | null>;
}

export class TeamCycleError extends Error {
  readonly code = "TEAM_TREE_CYCLE";
  constructor(teamId: string) {
    super(`Team hierarchy contains a cycle involving team ${teamId}`);
    this.name = "TeamCycleError";
  }
}

/**
 * Build parent/child maps and a DFS pre-order from flat team rows. Children
 * keep the input order (callers pass name-sorted rows). Nodes whose parent is
 * missing from the input are treated as roots (defensive — cross-client
 * parents are rejected at write time). Throws TeamCycleError on cycles, which
 * cannot be created through the server actions but would otherwise loop.
 */
export function buildTeamTree(teams: TeamNode[]): TeamTree {
  const ids = new Set(teams.map((t) => t.id));
  const childrenOf = new Map<string | null, string[]>();
  const parentOf = new Map<string, string | null>();

  for (const t of teams) {
    const parent = t.parentTeamId !== null && ids.has(t.parentTeamId) ? t.parentTeamId : null;
    parentOf.set(t.id, parent);
    const list = childrenOf.get(parent);
    if (list) list.push(t.id);
    else childrenOf.set(parent, [t.id]);
  }

  const dfsOrder: string[] = [];
  const depthOf = new Map<string, number>();

  const visit = (id: string, depth: number) => {
    depthOf.set(id, depth);
    dfsOrder.push(id);
    for (const child of childrenOf.get(id) ?? []) visit(child, depth + 1);
  };

  for (const rootId of childrenOf.get(null) ?? []) visit(rootId, 0);

  // Every node has exactly one parent entry, so root traversal visits each
  // node at most once — any node NOT reached from a root is part of a cycle.
  for (const t of teams) {
    if (!depthOf.has(t.id)) throw new TeamCycleError(t.id);
  }

  return { childrenOf, dfsOrder, depthOf, parentOf };
}

/** All ids in the subtree rooted at `teamId`, including `teamId` itself. */
export function subtreeIds(tree: TeamTree, teamId: string): Set<string> {
  const out = new Set<string>();
  const stack = [teamId];
  while (stack.length > 0) {
    const id = stack.pop();
    if (id === undefined || out.has(id)) continue;
    out.add(id);
    for (const child of tree.childrenOf.get(id) ?? []) stack.push(child);
  }
  return out;
}

/** True when `candidateId` is inside the subtree rooted at `teamId` (self counts). */
export function isInSubtree(tree: TeamTree, teamId: string, candidateId: string): boolean {
  return subtreeIds(tree, teamId).has(candidateId);
}

/**
 * Σ of `valueByTeam` over the subtree rooted at each node. Missing entries
 * count as 0. Returns a map covering every node in the tree.
 */
export function computeSubtreeSums(
  tree: TeamTree,
  valueByTeam: ReadonlyMap<string, number>,
): Map<string, number> {
  const sums = new Map<string, number>();
  // dfsOrder is pre-order; iterate in reverse so children resolve first.
  for (let i = tree.dfsOrder.length - 1; i >= 0; i--) {
    const id = tree.dfsOrder[i];
    let total = valueByTeam.get(id) ?? 0;
    for (const child of tree.childrenOf.get(id) ?? []) {
      total += sums.get(child) ?? 0;
    }
    sums.set(id, total);
  }
  return sums;
}
